/**
 * Выбор файла для кабинета менеджера (загрузка ирригации, размещение, акты).
 *
 * В «Основе форм» поля выбора файла нет, а голый `<input type="file">` — единственный нативный
 * контрол, который нельзя оформить: подпись «Choose File / No file chosen» берётся из языка
 * браузера, а не интерфейса. Здесь тот же выбор, но через `FileTrigger` react-aria: кнопка и
 * имя файла — наши, диалог выбора — системный.
 */
import { Button, FileTrigger } from 'react-aria-components';
import { FileUp, X } from 'lucide-react';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { Field } from '../../ui/forms';
import './file-pick.css';

const m = defineMessages({
  choose: { id: 'ops.filePick.choose', defaultMessage: 'Выбрать файл', description: 'Кнопка выбора файла (загрузка файла ирригации, размещения); коротко' },
  change: { id: 'ops.filePick.change', defaultMessage: 'Выбрать другой', description: 'Кнопка выбора файла, когда файл уже выбран: заменить его другим; коротко' },
  none: { id: 'ops.filePick.none', defaultMessage: 'Файл не выбран', description: 'Подпись рядом с кнопкой выбора файла, пока файл не выбран' },
  remove: { id: 'ops.filePick.remove', defaultMessage: 'Убрать файл {name}', description: 'aria-label крестика рядом с именем выбранного файла; {name} — имя файла' },
});

export interface FilePickProps {
  label: string;
  value: File | null;
  onChange: (file: File | null) => void;
  /** Типы для системного диалога: `.csv`, `text/csv`. */
  accept?: readonly string[];
  description?: string;
  isDisabled?: boolean;
}

export function FilePick({ label, value, onChange, accept, description, isDisabled }: FilePickProps) {
  const t = useT();
  return (
    <Field label={label} description={description}>
      <div className="filepick">
        <FileTrigger acceptedFileTypes={accept ? [...accept] : undefined} onSelect={(files) => onChange(files?.[0] ?? null)}>
          <Button className="btn btn--secondary btn--sm focus-ring filepick__btn" isDisabled={isDisabled}>
            <FileUp size={16} aria-hidden />
            {value ? t(m.change) : t(m.choose)}
          </Button>
        </FileTrigger>
        {value ? (
          <span className="filepick__name" title={value.name}>
            <span className="filepick__text">{value.name}</span>
            <Button className="filepick__clear focus-ring" aria-label={t(m.remove, { name: value.name })} onPress={() => onChange(null)} isDisabled={isDisabled}>
              <X size={14} aria-hidden />
            </Button>
          </span>
        ) : (
          <span className="filepick__none">{t(m.none)}</span>
        )}
      </div>
    </Field>
  );
}
